import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { OperationKeys } from '@decaf-ts/db-decorators';
import { Primitives } from '@decaf-ts/decorator-validation';
import { Dynamic, getModelAndRepository, getOnWindow, KeyValue } from '@decaf-ts/for-angular';
import { IonSpinner } from '@ionic/angular/standalone';
import { Leaflet, LeafletFile, Product } from '@pharmaledgerassoc/ptp-toolkit/shared';
import { XMLDisplayService } from '@pharmaledgerassoc/ptp-xml';
import { LeafletFileItem } from 'src/app/utils/types';

type ProductInformation = Pick<Product, 'nameMedicinalProduct' | 'inventedName'>;

@Dynamic()
@Component({
  selector: 'app-leaflet-preview',
  templateUrl: './leaflet-preview.component.html',
  styleUrls: ['./leaflet-preview.component.scss'],
  standalone: true,
  imports: [CommonModule, IonSpinner],
})
export class AppLeafletPreviewComponent implements OnInit {
  @ViewChild('component', { read: ElementRef, static: true })
  component!: ElementRef;

  content!: string;

  @Input()
  product: ProductInformation = {
    nameMedicinalProduct: 'Product ePI',
    inventedName: 'Product ePI',
  };

  @Input()
  value?: LeafletFileItem[] | string;

  @Input()
  data: LeafletFileItem[] = [];

  @Input()
  operation?: OperationKeys;

  @Input()
  language: string = 'en';

  @Input()
  model!: Leaflet;

  @Input()
  xmlService: XMLDisplayService = new XMLDisplayService('');

  changeDetectorRef = inject(ChangeDetectorRef);

  initialized = false;

  // constructor() {}

  async ngOnInit() {
    this.product = await this.getProduct();
    await this.initialize();
  }

  async initialize() {
    const { images, xmlContent } = this.parseValue();
    const content = await new XMLDisplayService('').display(xmlContent, images, this.product, this.language);
    if (this.component?.nativeElement) {
      this.component.nativeElement.innerHTML = '';
      this.component.nativeElement.append(content);
    }
    this.initialized = true;
    this.changeDetectorRef.detectChanges();
    // this.xmlContent = content;
  }

  async getProduct(): Promise<ProductInformation> {
    if (this.operation === OperationKeys.CREATE) {
      const product = getOnWindow('_lastProduct') as Product;
      if (product) {
        return product;
      }
      return { inventedName: 'Product ePI', nameMedicinalProduct: 'Product ePI' };
    }
    if (!this.initialized) {
      const { repository } = getModelAndRepository(Product.name) || {};
      if (repository) {
        const product = (await repository.read(this.model.productCode)) as Product;
        if (product) {
          return product;
        }
      }
    }
    return this.product;
  }

  parseValue(): { images: KeyValue; xmlContent: string } {
    if (this.operation !== OperationKeys.CREATE) {
      const files: LeafletFile[] = typeof this.value === Primitives.STRING ? JSON.parse(String(this.value)) : this.data;

      const xmlFile = files.find((file) => {
        const { fileName, fileContent } = file;
        if (fileName.includes('xml') || fileContent.includes('xml')) {
          return file;
        }
      });
      const images = files
        .filter((item) => {
          const { fileName } = item;
          if (!fileName.includes('.xml')) {
            return item;
          }
          return false;
        })
        .filter(Boolean);
      return { images, xmlContent: xmlFile?.fileContent || '' };
    } else {
      const files: LeafletFileItem[] = this.value === Primitives.STRING ? JSON.parse(String(this.value)) : this.data;

      const xmlFile = files.find((file) => {
        const { name, type } = file;
        if (name.includes('.xml') || (type && type?.includes('xml'))) {
          return file;
        }
      });
      const images = files.reduce<KeyValue>((acc, file) => {
        const { name } = file;
        if (!name.includes('.xml')) {
          acc[name] = file.source;
        }
        return acc;
      }, {});
      return { images, xmlContent: xmlFile?.source || '' };
    }
  }
}
